/**
 * Authorization contracts.
 *
 * `/auth/me` is the only place the frontend learns what it may do — the JWT
 * carries no permissions, deliberately, so decoding it would tell the client
 * nothing.
 */

import { z } from 'zod'

export const scopeType = z.enum(['OWN', 'ORG_UNIT', 'ORG_UNIT_TREE', 'ALL'])
export type ScopeType = z.infer<typeof scopeType>

export const orgUnitType = z.enum(['COMPANY', 'REGION', 'BRANCH', 'DEPARTMENT', 'TEAM'])
export type OrgUnitType = z.infer<typeof orgUnitType>

export const authProvider = z.enum(['ENTRA', 'LOCAL', 'SERVICE'])
export type AuthProvider = z.infer<typeof authProvider>

export const orgUnit = z.object({
  id: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  type: orgUnitType,
  name: z.string(),
  code: z.string(),
  path: z.string(),
  isActive: z.boolean(),
})
export type OrgUnit = z.infer<typeof orgUnit>

/** One granted scope: a role held at a place in the org tree. */
export const grantedScope = z.object({
  roleId: z.string().uuid(),
  roleName: z.string(),
  scopeType,
  orgUnitId: z.string().uuid().nullable(),
  orgPath: z.string().nullable(),
})
export type GrantedScope = z.infer<typeof grantedScope>

export const meResponse = z.object({
  user: z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().nullable(),
    upn: z.string().nullable(),
    authProvider,
    primaryOrgUnitId: z.string().uuid().nullable(),
    isBreakGlass: z.boolean(),
  }),
  /** Flat permission codes. The only thing `can()` consults. */
  permissions: z.array(z.string()),
  roles: z.array(z.string()),
  scopes: z.array(grantedScope),
  /** Module codes the user may reach at all — mirrors RLS `app.modules`. */
  modules: z.array(z.string()),
  /** Resource -> highest approval limit in minor units (paise). */
  approvalLimits: z.record(z.string(), z.number()),
})
export type MeResponse = z.infer<typeof meResponse>

export const loginStartResponse = z.object({ authorizeUrl: z.string().url() })
export type LoginStartResponse = z.infer<typeof loginStartResponse>

/**
 * Break-glass sign-in.
 *
 * `email` is validated as an identifier, NOT as an email address. The seeded
 * account is `break-glass@local`, which is deliberately not routable — no mail
 * should ever reach it — and a domain without a dot fails `z.string().email()`.
 *
 * Validating it as an email made the login impossible: the request was rejected
 * as malformed before any credential check ran, so the account that exists for
 * emergencies could never be used.
 */
export const breakGlassLogin = z.object({
  email: z.string().trim().toLowerCase().min(3).max(160),
  password: z.string().min(1),
  totp: z.string().trim().regex(/^\d{6}$/, 'Six digits'),
})
export type BreakGlassLogin = z.infer<typeof breakGlassLogin>
